import {
  VictoryAxis,
  VictoryBrushContainer,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryZoomContainer,
  DomainTuple
} from 'victory'
import { colorArray } from 'utils'
import { BrushZoomType, DataPointType } from '../../interfaces'
import BrushHandle from '../../components/BrushHandle'
import { memo, useState, useEffect, useId } from 'react'
import DownloadCSV from '../../../shared/DownloadCSV'
Math.log2 =
  Math.log2 ||
  function (x) {
    return Math.log(x) * Math.LOG2E
  }
const MIN = 6 * 30 * 24 * 60 * 60 * 1000
const MAX = 5 * 12 * 30 * 24 * 60 * 60 * 1000

const BrushZoom = memo(
  ({ data, width, filename, linkname, unit }: BrushZoomType) => {
    const [today] = useState(() => new Date().getTime())
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [csvData, setCsvData] = useState<
      {
        date: string
        volume: string
      }[]
    >()
    const [unfiltered, setUnfiltered] = useState<DataPointType[]>()
    const [points, setPoints] = useState<DataPointType[]>()
    const [brushDomain, setBrushDomain] = useState<{
      x: DomainTuple
      y?: DomainTuple
    }>(() => ({
      x: [today - MAX, today]
    }))

    useEffect(() => {
      if (data && !points) {
        const uf = data.flatMap((obj) =>
          obj.Data.map(({ x, y }) => ({
            x: new Date(obj.Year as number, (x as number) - 1, 1).getTime(),
            y
          }))
        )
        setUnfiltered(uf)
        const readable = uf.map(({ x, y }) => ({
          date: mapToIsoDate(new Date(x)),
          volume: `${y} ${unit}`
        }))
        setCsvData(readable)
        const k = Math.pow(2, Math.ceil(Math.log2(uf.length / 150)))
        const kfiltered = uf.filter((_, i) => i % k === 0)
        setPoints(kfiltered)
      }
    }, [data, points])

    const handleBrush = (domain: any) => {
      if (domain?.x[0] && domain?.x[1]) {
        // check is in bounds and zoom area isn't less than 6 months
        if (domain.x[1] - domain.x[0] > MIN && domain.x[1] <= today) {
          setBrushDomain(domain)
        }
      }
    }

    const mapToIsoDate = (date: Date) => {
      if (date) {
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        const day = date.getDate()
        return year + '-' + (month < 10 ? '0' : '') + month + '-' + day
      }
      return ''
    }
    const id = useId()

    return (
      <div>
        <div style={{ maxWidth: 750, position: 'relative' }}>
          {showOverlay && data && (
            <div className="wc-brush__overlay">
              <button className="wc-brush__btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20.003"
                  height="20"
                  viewBox="0 0 20.003 20"
                >
                  <g transform="translate(-39.776 -39.976)">
                    <path
                      d="M402.375,399.754a.618.618,0,0,0-.168-.573l-1.4-1.4-2.181-2.181,1.952-1.216.369-.229a.639.639,0,0,0-.153-1.161l-1.011-.333q-1.217-.4-2.437-.8l-2.937-.968-2.547-.839c-.409-.136-.818-.282-1.231-.407l-.017-.006a.64.64,0,0,0-.782.782c.11.337.222.674.333,1.011q.4,1.217.8,2.437.483,1.468.968,2.939l.839,2.547c.136.411.263.824.4,1.231l.006.017a.639.639,0,0,0,1.161.153l1.443-2.318.839.839,2.237,2.237.508.508a.643.643,0,0,0,.9,0l1.691-1.691.216-.216.029-.029v0A.637.637,0,0,0,402.375,399.754Zm-3.5-.045-2.237-2.237-.508-.508a.642.642,0,0,0-1,.129l-1.149,1.845q-.35-1.063-.7-2.127-.483-1.468-.968-2.939l-.839-2.547-.027-.082q1.14.375,2.281.752l2.937.968,2.478.816-1.482.923-.369.229a.644.644,0,0,0-.129,1l1.4,1.4,2.237,2.237.059.059-1.034,1.034Z"
                      transform="translate(-342.609 -342.222)"
                      fill="#333"
                    />
                    <path
                      d="M145.435,146l.261.261a.635.635,0,0,0,.9-.9l-1.843-1.843-.261-.261a.635.635,0,0,0-.9.9Z"
                      transform="translate(-101.437 -100.912)"
                      fill="#333"
                    />
                    <path
                      d="M147.957,541.891a.645.645,0,0,0-.9,0l-1.843,1.843-.261.261a.635.635,0,0,0,.9.9l1.843-1.843.261-.261A.643.643,0,0,0,147.957,541.891Z"
                      transform="translate(-102.771 -491.102)"
                      fill="#333"
                    />
                    <path
                      d="M543.455,146.294a.645.645,0,0,0,.9,0l1.843-1.843.261-.261a.635.635,0,0,0-.9-.9l-1.843,1.843-.261.261A.643.643,0,0,0,543.455,146.294Z"
                      transform="translate(-492.828 -100.948)"
                      fill="#333"
                    />
                    <path
                      d="M43.389,392.471a.636.636,0,1,0,0-1.271H40.413a.636.636,0,1,0,0,1.271h2.977Z"
                      transform="translate(0 -343.782)"
                      fill="#333"
                    />
                    <path
                      d="M393.436,44.225a.646.646,0,0,0,.636-.636V40.613a.636.636,0,1,0-1.271,0v2.977A.643.643,0,0,0,393.436,44.225Z"
                      transform="translate(-345.544 0)"
                      fill="#333"
                    />
                  </g>
                </svg>
                tap to navigate
              </button>
            </div>
          )}
          {/* @ts-ignore */}
          <VictoryChart
            width={width}
            height={470}
            style={{
              background: {
                fill: '#68D0EC',
                fillOpacity: 0.1,
                width: width - 15
              }
            }}
            padding={{
              left: 70,
              right: 10,
              top: 40,
              bottom: 70
            }}
            domainPadding={[50, 50]}
            scale={{ x: 'time' }}
            containerComponent={
              <VictoryZoomContainer
                responsive={false}
                zoomDimension="x"
                zoomDomain={brushDomain}
                /* @ts-ignore */
                minimumZoom={{ x: MIN }}
              />
            }
          >
            <VictoryLine
              data={unfiltered}
              style={{
                data: {
                  stroke: colorArray[0],
                  strokeWidth: 1
                }
              }}
            />
            <VictoryAxis
              offsetY={50}
              style={{
                axis: { stroke: 'transparent' },
                grid: { stroke: 'transparent', strokeWidth: 0 },
                ticks: { stroke: 'transparent' }
              }}
              tickLabelComponent={
                <VictoryLabel
                  id={(i) => `y:${id}:${i.index}`}
                  angle={width < 600 ? 45 : 0}
                  style={{
                    fontSize: 16,
                    fill: '#6E6E6E',
                    fontFamily: 'Arial',
                    fontWeight: 'bold'
                  }}
                />
              }
            />
            <VictoryAxis
              dependentAxis
              offsetX={78}
              tickFormat={(x) => `${x} ${unit}`}
              style={{
                axis: { stroke: 'transparent' },
                grid: {
                  stroke: '#C7C7C7',
                  strokeWidth: 1,
                  strokeDasharray: '6, 4'
                }
              }}
              tickLabelComponent={
                <VictoryLabel
                  id={(i) => `x:${id}:${i.index}`}
                  style={{
                    fontSize: 16,
                    fill: '#6E6E6E',
                    fontFamily: 'Arial',
                    fontWeight: 'bold'
                  }}
                />
              }
            />
          </VictoryChart>
          <VictoryChart
            padding={{
              top: 10,
              left: 70,
              right: 16,
              bottom: 60
            }}
            /* @ts-ignore */
            domainPadding={[5, 5]}
            width={width}
            height={165}
            scale={{ x: 'time' }}
            containerComponent={
              <VictoryBrushContainer
                brushDimension="x"
                brushDomain={brushDomain}
                brushStyle={{
                  stroke: 'transparent',
                  fill: '#68D0EC',
                  fillOpacity: 0.1
                }}
                onBrushDomainChange={handleBrush}
                // @ts-ignore
                handleComponent={<BrushHandle />}
                /* @ts-ignore */
                onBrushCleared={() => {
                  setShowOverlay(false)
                }}
              />
            }
          >
            <VictoryAxis
              tickFormat={(x) => new Date(x).getFullYear()}
              offsetY={40}
              style={{
                axis: { stroke: 'transparent' }
              }}
              tickLabelComponent={
                <VictoryLabel
                  id={(i) => `x2:${id}:${i.index}`}
                  angle={width < 600 ? 45 : 0}
                  style={{
                    fontSize: 16,
                    fill: '#6E6E6E',
                    fontFamily: 'Arial',
                    fontWeight: 'bold'
                  }}
                />
              }
            />
            <VictoryLine
              style={{
                data: {
                  stroke: colorArray[0],
                  strokeWidth: 1
                }
              }}
              data={points}
            />
          </VictoryChart>
        </div>
        <div className="w-full flex justify-end">
          {csvData && (
            <DownloadCSV
              jsonData={csvData}
              filename={filename}
              linkname={linkname}
            />
          )}
        </div>
      </div>
    )
  }
)

export default BrushZoom
